/**
 * New MoM — create a meeting with optional inline decisions.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ArrowLeft, Plus, Trash2, ClipboardList, Save } from 'lucide-react';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);

export default function MomNewPage() {
  const nav = useNavigate();
  const [types, setTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    meeting_date: today(),
    meeting_time: '',
    location: '',
    meeting_type: 'Management Review',
    chairperson: '',
    attendees: '',
    agenda: '',
    summary: '',
    tags: '',
  });
  const [decisions, setDecisions] = useState([]);

  useEffect(() => {
    api.get('/mom/meta').then(r => setTypes(r.data.meeting_types || [])).catch(() => {});
    api.get('/mom/people').then(r => setPeople(r.data.people || [])).catch(() => {});
  }, []);

  const addDecision = () => setDecisions(ds => [...ds, {
    decision: '', responsible_person: '', due_date: '', status: 'open', priority: 'medium', notes: '',
  }]);
  const updDecision = (i, patch) => setDecisions(ds => ds.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const rmDecision = (i) => setDecisions(ds => ds.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.meeting_date) { toast.error('Meeting date is required'); return; }
    const cleanDecisions = decisions
      .filter(d => d.decision.trim())
      .map(d => ({
        decision: d.decision.trim(),
        responsible_person: d.responsible_person.trim(),
        due_date: d.due_date || null,
        status: d.status || 'open',
        priority: d.priority || 'medium',
        notes: d.notes || '',
      }));
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        meeting_date: form.meeting_date,
        meeting_time: form.meeting_time || null,
        location: form.location,
        meeting_type: form.meeting_type,
        chairperson: form.chairperson,
        attendees: form.attendees.split(',').map(s => s.trim()).filter(Boolean),
        agenda: form.agenda,
        summary: form.summary,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        decisions: cleanDecisions,
      };
      const r = await api.post('/mom/meetings', payload);
      toast.success('Meeting created');
      nav(`/training/mom/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-16">
      <div className="max-w-[1100px] mx-auto px-6 pt-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => nav('/training/mom')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" size="sm" onClick={save} disabled={saving} data-testid="mom-new-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Create MoM'}
          </Button>
        </div>

        <Card className="p-6 mb-5 border-slate-200 shadow-sm bg-white">
          <h1 className="text-xl font-bold text-slate-900 mb-4">New Minutes of Meeting</h1>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Weekly Operations Sync" data-testid="mom-new-title" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input type="time" value={form.meeting_time} onChange={e => setForm({ ...form, meeting_time: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.meeting_type} onValueChange={v => setForm({ ...form, meeting_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Boardroom / MS Teams" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Chairperson</Label>
                <Input value={form.chairperson} onChange={e => setForm({ ...form, chairperson: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Attendees (comma-separated)</Label>
                <Input value={form.attendees} onChange={e => setForm({ ...form, attendees: e.target.value })} placeholder="Anupam, Priya, Neha" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Agenda</Label>
              <Textarea rows={3} value={form.agenda} onChange={e => setForm({ ...form, agenda: e.target.value })} placeholder="Points to discuss…" />
            </div>
            <div>
              <Label className="text-xs">Summary / Key Notes</Label>
              <Textarea rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="Outcomes, highlights…" />
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="Q1, Review, Revenue" />
            </div>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-indigo-600" /> Decisions & Action Items
              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{decisions.length}</Badge>
            </h2>
            <Button size="sm" variant="outline" onClick={addDecision} data-testid="mom-new-add-decision">
              <Plus className="w-4 h-4 mr-1.5" /> Add action item
            </Button>
          </div>
          {decisions.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-4">You can add action items now, or later from the meeting page.</p>
          ) : (
            <div className="space-y-3">
              {decisions.map((d, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-1.5">
                      {i + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Textarea rows={2} value={d.decision} onChange={e => updDecision(i, { decision: e.target.value })} placeholder="What needs to be done?" />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide">Responsible</Label>
                          <Input list="mom-new-people" value={d.responsible_person} onChange={e => updDecision(i, { responsible_person: e.target.value })} placeholder="Name" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide">Due</Label>
                          <Input type="date" value={d.due_date} onChange={e => updDecision(i, { due_date: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide">Priority</Label>
                          <Select value={d.priority} onValueChange={v => updDecision(i, { priority: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide">Status</Label>
                          <Select value={d.status} onValueChange={v => updDecision(i, { status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="done">Completed</SelectItem>
                              <SelectItem value="blocked">Blocked</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Input value={d.notes} onChange={e => updDecision(i, { notes: e.target.value })} placeholder="Notes (optional)" />
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => rmDecision(i)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <datalist id="mom-new-people">
                {people.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
